import { Subscribable } from '@benzinga/subscribable';
import { Session } from '@benzinga/session';
import { BzDateTime, TimeFormat } from '@benzinga/date-utils';
import { TimeZoneName } from '@vvo/tzdb';
import { ProUserSettingsManager } from '@benzinga/pro-user-settings';

interface TimezoneChangedEvent {
  timezone: TimeZoneName;
  type: 'time:time_zone_changed';
}

interface TimeFormatChangedEvent {
  timeFormat: TimeFormat;
  type: 'time:time_format_changed';
}

export type TimeManagerEvent = TimezoneChangedEvent | TimeFormatChangedEvent;

export type Day = 'yesterday' | 'today' | 'tomorrow';
const DAY_MAPPER: Record<Day, number> = {
  today: 0,
  tomorrow: 1,
  yesterday: -1,
};

export class TimeManager extends Subscribable<TimeManagerEvent> {
  private timeFormat: TimeFormat;
  private timeOffset: number;
  private timeOffsetFromEastern: number;
  private timezone: TimeZoneName;

  constructor(session: Session) {
    super();
    session.getManager(ProUserSettingsManager).subscribe(event => {
      switch (event.type) {
        case 'pro_user_settings:update':
          this.setTimezone(event.setting.timezone);
          this.setTimeFormat(event.setting.timeFormat);
      }
    });
    this.timeFormat = session.getManager(ProUserSettingsManager).getSettings().timeFormat;
    this.timezone = session.getManager(ProUserSettingsManager).getSettings().timezone;
    this.timeOffset = this.getTimezoneOffset(this.timezone);
    this.timeOffsetFromEastern = this.timeOffset - this.getTimezoneOffset('America/New_York');
  }

  public static getName = () => 'benzinga-time';

  public getTime = () => ({
    timeFormat: this.timeFormat,
    timeOffset: this.timeOffset,
    timeOffsetFromEastern: this.timeOffsetFromEastern,
    timezone: this.timezone,
  });
  public getTimeFormat = (): TimeFormat => this.timeFormat;
  public getTimeOffset = (): number => this.timeOffset;
  public getTimeOffsetFromEastern = (): number => this.timeOffsetFromEastern;
  public getTimezone = (): TimeZoneName => this.timezone;

  public setTimeFormat = (timeFormat: TimeFormat): void => {
    if (this.timeFormat !== timeFormat) {
      this.timeFormat = timeFormat;
      this.dispatch({
        timeFormat: timeFormat,
        type: 'time:time_format_changed',
      });
    }
  };

  public setTimezone = (timezone = 'America/New_York'): void => {
    if (this.timezone !== timezone) {
      this.timezone = timezone;
      this.timeOffset = this.getTimezoneOffset(timezone);
      this.timeOffsetFromEastern = this.timeOffset - this.getTimezoneOffset('America/New_York');
      this.dispatch({
        timezone: timezone,
        type: 'time:time_zone_changed',
      });
    }
  };

  /**
   * @param day accepts 'yesterday' | 'today' | 'tomorrow'
   *
   * @remarks
   * check if the date is today, tomorrow, yesterday\<
   */
  public isDay = (date: BzDateTime, day: Day | number): boolean => {
    const dayOffset = typeof day === 'string' ? DAY_MAPPER[day] : day;
    const dayToCheck = new BzDateTime(new BzDateTime().toLocaleString('en', { timeZone: this.timezone }));

    dayToCheck.setDate(dayToCheck.getDate() + dayOffset);
    const dayStartUTC = new BzDateTime(dayToCheck.getFullYear(), dayToCheck.getMonth(), dayToCheck.getDate()).getTime();

    dayToCheck.setDate(dayToCheck.getDate() + 1);
    const dayEndUTC = new BzDateTime(dayToCheck.getFullYear(), dayToCheck.getMonth(), dayToCheck.getDate()).getTime();

    return dayStartUTC <= date.getTime() && date.getTime() < dayEndUTC;
  };

  private getTimezoneOffset = (timeZone: string) => {
    const date = new BzDateTime();
    const offset =
      Date.parse(date.toLocaleString('en', { timeZone })) - Date.parse(date.toLocaleString('en', { timeZone: 'utc' }));

    // return UTC offset in millis
    return offset / 60000;
  };
}
